import { useRef } from "react";
import Button from "components/button";
import Dialog from "components/dialog";

/**
 * ConfirmDialog — small yes/no dialog. Prefer `useConfirm()` for imperative flows.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.message]
 * @param {string} [props.confirmLabel="Confirm"]
 * @param {string} [props.cancelLabel="Cancel"]
 * @param {"default"|"danger"} [props.tone="default"] `danger` renders a danger confirm button and focuses Cancel on open.
 * @param {boolean} [props.loading=false] Spinner on the confirm button; Cancel, Esc and overlay close are disabled.
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onCancel Called by Cancel, Esc, overlay click and the close button.
 */
function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  loading = false,
  onConfirm,
  onCancel,
}) {
  const cancelRef = useRef(null);
  const isDanger = tone === "danger";

  const handleClose = () => {
    if (!loading) onCancel();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="sm"
      initialFocusRef={isDanger ? cancelRef : undefined}
      footer={
        <>
          <Button
            ref={cancelRef}
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={isDanger ? "danger" : "primary"}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <div className="text-sm text-fg-muted">{message}</div>}
    </Dialog>
  );
}

export default ConfirmDialog;
